package org.catharsis.spendtel.core.arch

import androidx.annotation.CallSuper
import androidx.annotation.VisibleForTesting
import androidx.lifecycle.SavedStateHandle
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.CoroutineStart
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.launch
import kotlin.coroutines.CoroutineContext
import kotlin.coroutines.EmptyCoroutineContext
import kotlin.jvm.JvmStatic

abstract class MviViewModel<EVENT : ViewEvent, STATE : ViewState, EFFECT : ViewEffect>
constructor(
    private val savedStateHandle: SavedStateHandle?,
    initialState: STATE
) : ViewModel(), ObservableDisposable {

    constructor(
        initialState: STATE
    ): this(
        savedStateHandle = null,
        initialState = initialState
    )

    /**
     * Tracks if [state] currently has subscribers
     */
    private var isSubscribed = false
    private val _state by lazy {
        @Suppress("UNCHECKED_CAST")
      //  val savedState = savedStateHandle?.savedViewState as? STATE

        MutableStateFlow(initialState)
    }

    /**
     * [StateFlow] representing the current observable UI state. Typically this State would be be
     * represented as a sealed class of immutable State objects (i.e. ExampleState.Loading,
     * ExampleState.Loaded etc) that are collected and rendered in a @Composable
     */
    val state: StateFlow<STATE>
        get() = _state

    /**
     * Alias for [MviViewModel.state.value], use to get the current UI state
     */
    @Deprecated(
        message = "Accessing this directly from a @Composable completely bypasses state snapshot observability.\n" +
                "For usage within a @Composable, use [viewModel.rememberState()] or [viewModel.state.collectAsState()] to obtain a [State] snapshot.\n" +
                "For usage within a MviViewModel subclass, use the protected method [latestState].\n",
        replaceWith = ReplaceWith("latestState"),
        level = DeprecationLevel.WARNING
    )
    @VisibleForTesting
    val currentState: STATE
        get() = _state.value

    /**
     * Alias for [MviViewModel.state.value], use to get the current UI state
     */
    protected val latestState: STATE
        get() = _state.value

    private val _event = MutableSharedFlow<EVENT>()
    private val _effect = Channel<EFFECT>()

    /**
     * An effect is a one-time side effect, such as a navigation request or an error SnackBar.
     * Typically used with [androidx.compose.runtime.LaunchedEffect]
     */
    val effect: Flow<EFFECT> = _effect.receiveAsFlow()

    init {
        log("init")
        addSubscriptionCallbacks()
        collectViewEvents()
    }

    /**
     * Process user events
     */
    private fun collectViewEvents() {
        launch {
            _event.collect {
                log("received event: $it")
                receiveEvent(it)
            }
        }
    }

    private fun addSubscriptionCallbacks() {
        launch {
            _state.subscriptionCount.collect { count ->
                if (count == 1 && !isSubscribed) {
                    log("onSubscribed")
                    onSubscribed()
                } else if (count == 0 && isSubscribed) {
                    log("onUnsubscribed")
                    onUnsubscribed()
                }
                isSubscribed = (count > 0)
            }
        }
        addCloseable(AutoCloseable { if (isSubscribed) {
            // this ensures that we get a call to onUnsubscribed when the VM is cleared
            log("onUnsubscribed")
            onUnsubscribed()
        }  })
    }

    /**
     * Called when the active subscriber count of [state] becomes non-zero.
     */
    protected open fun onSubscribed() {
    }

    /**
     * Called when the active subscriber count of [state] becomes zero
     */
    protected open fun onUnsubscribed() {
    }

    override fun onDispose(block: () -> Unit) = addCloseable(AutoCloseable { block() })

    /**
     * Called when the ViewModel will be disposed
     */
    @CallSuper
    override fun onCleared() {
        super.onCleared()
        log("onCleared")
    }

    /**
     * Post an event for handling by the ViewModel
     */
    fun postEvent(event: EVENT) {
        launch {
            _event.emit(event)
        }
    }

    protected abstract fun receiveEvent(event: EVENT)

    /**
     * Update the current state via a reducer function
     */
    protected fun setState(reducer: STATE.() -> STATE) {
        val nextState = latestState.reducer()
        _state.value = nextState
    }

    /**
     * Notify the collecting @Composable of a one-time SideEffect
     */
    protected fun emitEffect(builder: () -> EFFECT) {
        val nextEffect = builder()
        launch {
            log("effect emitted: $nextEffect")
            _effect.send(nextEffect)
        }
    }

    protected fun launch(
        context: CoroutineContext = EmptyCoroutineContext,
        start: CoroutineStart = CoroutineStart.DEFAULT,
        block: suspend CoroutineScope.() -> Unit
    ) = viewModelScope.launch(context, start, block)

    private fun log(message: String) {
        //Timber.tag(this::class.java.simpleName).v(message)
    }

    companion object {
        /**
         * Used to get/set the unique key for this [MviViewModel] instance, if any, from a [SavedStateHandle]
         */
        const val ARG_VIEW_MODEL_KEY = "MviViewModel.VIEW_MODEL_KEY"
        private const val ARG_SAVED_VIEW_STATE = "MviViewModel.SAVED_VIEW_STATE"

        @JvmStatic
        protected val SavedStateHandle.viewModelKey: String? get() = get<String>(ARG_VIEW_MODEL_KEY)

//        @JvmStatic
//        private val SavedStateHandle.savedViewState: Parcelable? get() = get<Parcelable>(
//            ARG_SAVED_VIEW_STATE)
    }
}
